import axios from 'axios';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import LicenseContext from 'src/contexts/LicenseContext';
import { useTruckShiftUpdate } from 'src/contexts/TruckShiftUpdateContext';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { checkBulkApplyConflict } from 'src/hooks/useMutationBulkApply';
import { checkBulkRemoveConflict } from 'src/hooks/useMutationBulkRemove';
import { useMutationDriver } from 'src/hooks/useMutationDriver';
import { useMutationShiftPatterns } from 'src/hooks/useMutationShiftPatterns';
import { useMutationTruckShift } from 'src/hooks/useMutationTruckShift';
import { useMutationTruckShiftImport } from 'src/hooks/useMutationTruckShiftImport';
import { useMutationTruckShiftTruncate } from 'src/hooks/useMutationTruckShiftTruncate';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';
import { useQueryShiftPatterns } from 'src/hooks/useQueryShiftPatterns';
import { useQueryTruckShifts } from 'src/hooks/useQueryTruckShifts';
import {
  buildBulkRequest, buildOverwriteRetryBody, buildRemoveRequest, BulkApplyRequestBody, BulkConflict, BulkRemoveRequestBody, GridSelection, intersectSet, parseConflictResponse, toggleSetValue,
} from 'src/pages/TruckShifts/bulkSelection';
import { AddRequestBody, EditRequestBody, editablePatternOptions } from 'src/pages/TruckShifts/cellEditor';
import { extractApiErrorMessages, notifyBulkError } from 'src/pages/TruckShifts/notifyBulkError';
import { PatternFormValues, toRequestBody } from 'src/pages/TruckShifts/patternForm';
import { TruckShiftsPresenter } from 'src/pages/TruckShifts/Presenter';
import { buildCopyRequest, countExistingShifts, previousWeekRange } from 'src/pages/TruckShifts/truckShiftCopy';
import { downloadTruckShiftsExport } from 'src/pages/TruckShifts/truckShiftExport';
import {
  applyFilters, backDestinationPath, buildDateColumns, countSelectedShifts, deriveFilterOptions, isNullableNumber, mapShiftsToCells, normalizePeriod, periodLabel, PeriodMode,
  resolvePatternSelection, shiftRange, ShiftCell, toggleRange, TruckFilters,
} from 'src/pages/TruckShifts/truckShiftGrid';

// 便プルダウンの既定便（PM 指定）。未選択時はこの便を初期選択にする。
const DEFAULT_PATTERN_NAME = '全日便';

// 勤務計画画面の操作を GA に送信する（他画面と同一の screen_name/button_name 形式）。
const gaEvent = (action: string, buttonName: string) => {
  ReactGA.event(action, { screen_name: SCREEN_NAMES.TRUCK_SHIFTS, button_name: buttonName });
};

type Props = {
  // initialStartOn/initialEndOn は URL 範囲＝呼び出し元（配車画面）の範囲。戻り先に使う。表示範囲ではない。
  initialStartOn: string;
  initialEndOn: string;
  initialPeriod: PeriodMode;
  backTo?: string;
  onPeriodPersist?: (period: PeriodMode) => void;
};

// 一括反映(apply)／一括解除(remove)の上書き/解除確認 state。action で body 型を narrowing する。
type ConfirmState =
  | { action: 'apply'; body: BulkApplyRequestBody; conflict: BulkConflict }
  | { action: 'remove'; body: BulkRemoveRequestBody; conflict: BulkConflict };

const Component: FC<Props> = memo(({ initialStartOn, initialEndOn, initialPeriod, backTo, onPeriodPersist }) => {
  const context = useContext(LicenseContext);
  const navigate = useNavigate();

  // 表示範囲は URL 範囲(initialStartOn)を anchor に週/月へ正規化した範囲を初期値とする。
  // 期間ナビ（前/次/週月切替）は URL を書き換えず state のみ更新する（/shifts の週送りと同じ）。
  const initialDisplay = normalizePeriod(initialPeriod, initialStartOn);
  const [period, setPeriod] = useState<PeriodMode>(initialPeriod);
  const [startOn, setStartOn] = useState<string>(initialDisplay.startOn);
  const [endOn, setEndOn] = useState<string>(initialDisplay.endOn);
  const selectedCompanyId = context.config?.selected_company_id ?? 0;

  const [filters, setFilters] = useState<TruckFilters>({ partnerCompanyIds: [], klasses: [], search: '' });
  const [openCell, setOpenCell] = useState<{ truckId: number; date: string; anchorEl: HTMLElement } | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const [patternManagerOpen, setPatternManagerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const importMutation = useMutationTruckShiftImport();
  const [copyOpen, setCopyOpen] = useState(false);

  const [selectedTruckIds, setSelectedTruckIds] = useState<Set<number>>(new Set());
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  // 選択便を localStorage に永続化。復元値は便一覧ロード後に検証して無効なら未選択へ戻す（下の useEffect）。
  const [activePatternId, setActivePatternId] = usePersistedJSON<number | null>('truckShifts.activePatternId', null, isNullableNumber);
  // mutation は App レベルの TruckShiftUpdateProvider が保持する（稼働表を離れても実行が継続し、
  // 別画面でも右下の進捗表示・invalidate が働くようにするため。React Query v4 は mutation を
  // 実行元 component の observer に紐づけるため、ここで useMutation を呼ぶと unmount で追跡が切れる）。
  // isUpdating（稼働表更新中）は App レベルの Provider が保持する isLoading。別画面でも継続する。
  const { bulkApply, bulkRemove, copyMutation, isUpdating } = useTruckShiftUpdate();
  // 更新が始まったら開いている編集ポップオーバーを閉じる（更新中の編集・保存を防ぐ）。
  useEffect(() => { if (isUpdating) setOpenCell(null); }, [isUpdating]);
  // 反映/解除の実行前に衝突チェック（/bulk/check・/bulk_removal/check）を投げる間の busy 状態。
  const [checking, setChecking] = useState(false);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const { data, isLoading, isError } = useQueryTruckShifts(selectedCompanyId, startOn, endOn, !!context.config);
  const { data: patternData } = useQueryShiftPatterns(selectedCompanyId, !!context.config);
  const patterns = useMemo(() => patternData ?? [], [patternData]);
  const { create: createPattern, update: updatePattern, destroy: destroyPattern } = useMutationShiftPatterns();

  const { edit, remove, assign, addShift } = useMutationTruckShift();
  const truncate = useMutationTruckShiftTruncate();
  const createDriverMutation = useMutationDriver();
  const { data: driverData } = useDriversQuery(startOn, { enabled: !!context.config && openCell !== null, allVisible: true });
  const driverOptions = useMemo(() => (driverData ?? []).filter((d) => !d.temporary), [driverData]);
  const patternOptions = useMemo(() => editablePatternOptions(patterns), [patterns]);

  const trucks = useMemo(() => data ?? [], [data]);
  const filteredTrucks = useMemo(() => applyFilters(trucks, filters), [trucks, filters]);
  const filterOptions = useMemo(() => deriveFilterOptions(trucks), [trucks]);
  const dateColumns = useMemo(() => buildDateColumns(startOn, endOn), [startOn, endOn]);
  const copySource = useMemo(() => previousWeekRange({ startOn, endOn }), [startOn, endOn]);
  const existingCount = useMemo(() => countExistingShifts(filteredTrucks), [filteredTrucks]);
  const removeCount = useMemo(
    () => countSelectedShifts(trucks, selectedTruckIds, selectedDates),
    [trucks, selectedTruckIds, selectedDates],
  );

  // P1: 期間移動（dateColumns 変化）・フィルタ/会社切替/再取得（filteredTrucks 変化）で
  // 「現在の表示集合」から外れた選択を取り除き、画面に見えない日付・トラックへ一括反映する事故を防ぐ。
  // intersectSet は変化が無ければ同一参照を返すので不要な再レンダーは起きない。
  useEffect(() => {
    setSelectedDates((prev) => intersectSet(prev, dateColumns));
  }, [dateColumns]);
  useEffect(() => {
    setSelectedTruckIds((prev) => intersectSet(prev, filteredTrucks.map((t) => t.id)));
  }, [filteredTrucks]);

  // 便一覧ロード後、前回選択が有効ならそれを維持し、無効/未選択なら既定便（全日便）にフォールバック。
  // 会社切替・便削除にも追従する。patternData 未取得(undefined)の間はロード前なのでリセットしない。
  useEffect(() => {
    if (patternData === undefined) return;
    setActivePatternId((prev) => resolvePatternSelection(prev, patternData, DEFAULT_PATTERN_NAME));
  }, [patternData, setActivePatternId]);

  // toggleTruck/setTrucksSelected/toggleDate は setSelectedTruckIds/setSelectedDates の関数型 setter
  // だけを使うため selectedTruckIds/selectedDates に依存しない。useCallback で参照を固定し、
  // memo 化した CompanyGroupPresenter/BodyRowPresenter へ安定コールバックとして直接渡せるようにする
  // （selection オブジェクト全体を経由すると selectedTruckIds/selectedDates の変化で毎回新しい
  // 関数参照になり、配下の全再レンダーを引き起こしてしまう）。
  const toggleTruck = useCallback((id: number) => setSelectedTruckIds((prev) => toggleSetValue(prev, id)), []);
  const setTrucksSelected = useCallback((ids: number[], sel: boolean) => setSelectedTruckIds((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => { if (sel) next.add(id); else next.delete(id); });
    return next;
  }), []);
  const toggleDate = useCallback((date: string) => setSelectedDates((prev) => toggleSetValue(prev, date)), []);

  const selection: GridSelection = useMemo(() => ({
    isTruckSelected: (id) => selectedTruckIds.has(id),
    isDateSelected: (date) => selectedDates.has(date),
    toggleTruck,
    setTrucksSelected,
    toggleDate,
  }), [selectedTruckIds, selectedDates, toggleTruck, setTrucksSelected, toggleDate]);

  const extractError = (rejection: PromiseRejectedResult): string => {
    const error = rejection.reason as unknown;
    if (axios.isAxiosError(error)) {
      const messages = extractApiErrorMessages(error.response?.data);
      if (messages.length > 0) return messages.join(' / ');
      const status = error.response?.status;
      return status ? `エラー ${status}` : '操作に失敗しました';
    }
    return '操作に失敗しました';
  };

  // 事業所未選択（全社表示 = selected_company_id 0）では export/import を許可しない
  const noCompanySelected = selectedCompanyId === 0;
  const importDisabled = noCompanySelected;
  const exportDisabled = exporting || filteredTrucks.length === 0 || noCompanySelected;
  const handleExport = async () => {
    if (noCompanySelected || filteredTrucks.length === 0) return; // 事業所未選択 / 0 件は止める（全件 export 事故防止）
    setExporting(true);
    try {
      await downloadTruckShiftsExport({ startOn, endOn, truckIds: filteredTrucks.map((t) => t.id) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'エクスポートに失敗しました';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const runImport = (args: { file: File; commit: boolean }) => {
    gaEvent('apply', args.commit ? 'インポート反映' : 'インポート確認');
    return importMutation.mutateAsync(args);
  };
  const extractImportError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
      const messages = extractApiErrorMessages(error.response?.data);
      if (messages.length > 0) return messages.join(' / ');
      const status = error.response?.status;
      return status ? `インポートに失敗しました（エラー ${status}）` : 'インポートに失敗しました';
    }
    return 'インポートに失敗しました';
  };

  // CompanyGroup/BodyRow は memo 化されており onCellClick を props として辿って渡している。
  // プレーン関数だと Component 再レンダーごとに新しい参照になり、トラック1台の操作でも
  // 全行の memo bail-out が失敗する（C3 の目的=操作した行だけ再レンダーが未達になる）。
  // setOpenCell(useState のセッター)は安定参照のため依存配列は空でよい。
  const handleCellClick = useCallback((truckId: number, date: string, _cells: ShiftCell[], anchorEl: HTMLElement) => {
    if (isUpdating) { enqueueSnackbar('処理中です。完了後に操作してください。', { variant: 'warning' }); return; }
    setOpenCell({ truckId, date, anchorEl });
  }, [isUpdating, enqueueSnackbar]);

  const cellPopover = {
    open: openCell !== null,
    anchorEl: openCell?.anchorEl ?? null,
    truckId: openCell?.truckId ?? 0,
    date: openCell?.date ?? '',
    truckName: (openCell ? trucks.find((t) => t.id === openCell.truckId)?.licensePlateValue ?? '' : ''),
    cells: (() => {
      if (!openCell) return [];
      const truck = trucks.find((t) => t.id === openCell.truckId);
      if (!truck) return [];
      return mapShiftsToCells(truck.shifts, [openCell.date])[openCell.date] ?? [];
    })(),
    patternOptions,
    allPatterns: patterns,
    driverOptions,
    onClose: () => setOpenCell(null),
    editShift: (id: number, body: EditRequestBody) => {
      gaEvent('apply', 'セル編集');
      return edit.mutateAsync({ id, body });
    },
    assignDriver: (id: number, driverId: number | null) => {
      gaEvent('apply', 'ドライバー割当');
      return assign.mutateAsync({ id, driverId });
    },
    createDriver: (name: string) => {
      gaEvent('apply', 'ドライバー新規作成');
      return createDriverMutation.mutateAsync(name);
    },
    addShift: (body: AddRequestBody) => {
      gaEvent('apply', 'セル勤務追加');
      return addShift.mutateAsync(body);
    },
    deleteShift: (id: number) => {
      gaEvent('apply', 'セル勤務削除');
      return remove.mutateAsync(id);
    },
    truncateShift: (id: number, on: string) => {
      gaEvent('apply', '勤務打ち切り');
      return truncate.mutateAsync({ id, truncateOn: on });
    },
    onNotify: (message: string, variant: 'success' | 'error') => enqueueSnackbar(message, { variant }),
    extractError,
  };

  const submitBulk = async (body: BulkApplyRequestBody) => {
    try {
      const result = await bulkApply.mutateAsync(body);
      const noteParts = [];
      if (result.deleted_count > 0) noteParts.push(`${result.deleted_count}件を削除`);
      if ((result.split_count ?? 0) > 0) noteParts.push(`${result.split_count}件を分割保持`);
      const note = noteParts.length > 0 ? `（${noteParts.join('・')}）` : '';
      enqueueSnackbar(`${result.created_count}件を反映しました${note}`, { variant: 'success' });
      setConfirm(null);
    } catch (error) {
      const conflict = axios.isAxiosError(error) ? parseConflictResponse(error.response?.status, error.response?.data) : null;
      if (conflict) {
        setConfirm({ action: 'apply', body, conflict });
        return;
      }
      notifyBulkError('反映', error, enqueueSnackbar);
      // 終端エラー（再実行しても同じ）は確認ダイアログを閉じる。再衝突(409)は上で return 済みなので閉じない。
      setConfirm(null);
    }
  };

  const submitRemove = async (body: BulkRemoveRequestBody) => {
    try {
      const result = await bulkRemove.mutateAsync(body);
      const splitNote = (result.split_count ?? 0) > 0 ? `（${result.split_count}件を分割保持）` : '';
      enqueueSnackbar(`${result.deleted_count}件を解除しました${splitNote}`, { variant: 'success' });
      setConfirm(null);
    } catch (error) {
      const conflict = axios.isAxiosError(error) ? parseConflictResponse(error.response?.status, error.response?.data) : null;
      if (conflict) {
        setConfirm({ action: 'remove', body, conflict });
        return;
      }
      // 既存挙動を維持: messages が空でもステータスは付与しない（他3経路と異なる）。
      notifyBulkError('解除', error, enqueueSnackbar, { includeStatus: false });
      // 終端エラー（再実行しても同じ）は確認ダイアログを閉じる。再衝突(409)は上で return 済みなので閉じない。
      setConfirm(null);
    }
  };

  // 反映と解除は同じ選択範囲へ /bulk と /bulk_removal を並行発行し得るため、どちらか実行中は両方を弾く。
  // チェック中(checking)も同様に弾く。
  const bulkBusy = bulkApply.isPending || bulkRemove.isPending || checking;

  // 実処理の前に衝突チェックを 200 で取得。衝突あれば確認ダイアログ、無ければそのまま実行する
  // （routine では実 API が 409 を返さない＝ログがエラー化しない。レース時は submitBulk 内の 409 で保険）。
  const handleApply = async () => {
    if (activePatternId == null || bulkBusy) return;
    if (isUpdating) { enqueueSnackbar('処理中です。完了後に操作してください。', { variant: 'warning' }); return; }
    const body = buildBulkRequest(selectedTruckIds, selectedDates, activePatternId);
    setChecking(true);
    try {
      const conflict = await checkBulkApplyConflict(body);
      if (conflict) {
        setConfirm({ action: 'apply', body, conflict });
        return;
      }
      await submitBulk(body);
    } catch (error) {
      notifyBulkError('反映', error, enqueueSnackbar);
    } finally {
      setChecking(false);
    }
  };

  const handleRemove = async () => {
    if (bulkBusy) return;
    if (isUpdating) { enqueueSnackbar('処理中です。完了後に操作してください。', { variant: 'warning' }); return; }
    const body = buildRemoveRequest(selectedTruckIds, selectedDates);
    setChecking(true);
    try {
      const conflict = await checkBulkRemoveConflict(body);
      if (conflict) {
        setConfirm({ action: 'remove', body, conflict });
        return;
      }
      await submitRemove(body);
    } catch (error) {
      notifyBulkError('解除', error, enqueueSnackbar);
    } finally {
      setChecking(false);
    }
  };

  const handleConfirm = (mode: 'whole' | 'split') => {
    if (!confirm) return;
    const { action, body } = confirm;
    // 上書き/一括解除は時間がかかるため、OK 押下で確認ダイアログを即閉じて画面を操作できるようにする。
    // 進捗は右下の「稼働表を更新中」表示（非ブロッキング）で示す。再衝突(409)時は submit 側が再度ダイアログを開く。
    setConfirm(null);
    if (action === 'apply') void submitBulk(buildOverwriteRetryBody(body, mode));
    else void submitRemove(buildOverwriteRetryBody(body, mode));
  };

  const handleCopy = () => {
    const truckIds = filteredTrucks.map((t) => t.id);
    if (noCompanySelected || truckIds.length === 0) return; // 事業所未選択 / 0 件は API に空配列を投げない
    if (isUpdating) { enqueueSnackbar('処理中です。完了後に操作してください。', { variant: 'warning' }); return; }
    // コピーは台数が多いと時間がかかるため、ダイアログを即閉じて右下「稼働表を更新中」で進捗を示す（非ブロッキング）。
    // mutation は mutationKey で App レベルの進捗表示・編集ロック・MutationCache の invalidate に載る。
    setCopyOpen(false);
    copyMutation.mutate(
      buildCopyRequest({ truckIds, source: copySource, targetStartOn: startOn }),
      {
        onSuccess: (result) => {
          enqueueSnackbar(
            `先週の稼働表をコピーしました（作成 ${result.created_count} / 削除 ${result.deleted_count}）`,
            { variant: 'success' },
          );
        },
        onError: (error) => { notifyBulkError('コピー', error, enqueueSnackbar); },
      },
    );
  };

  // 期間移動は表示 state のみ更新。URL は呼び出し元の戻り範囲を保持するため書き換えない。
  const navigateTo = (next: { startOn: string; endOn: string }) => {
    setStartOn(next.startOn);
    setEndOn(next.endOn);
  };

  const onPrev = () => navigateTo(shiftRange(period, startOn, -1));
  const onNext = () => navigateTo(shiftRange(period, startOn, 1));
  const onToday = () => {
    // 実際の今日（Asia/Tokyo）を含む現在の週/月へ移動。現在の period を保持する。
    const today = format(new Date(), 'yyyy-MM-dd', { timeZone: 'Asia/Tokyo' });
    navigateTo(normalizePeriod(period, today));
  };
  const onPeriodChange = (nextPeriod: PeriodMode) => {
    setPeriod(nextPeriod);
    navigateTo(toggleRange(nextPeriod, startOn));
    onPeriodPersist?.(nextPeriod);
  };

  const rangeLabel = periodLabel(period, startOn, endOn);

  const nextSortOrder = () => Math.max(0, ...patterns.map((p) => p.sortOrder ?? 0)) + 1;

  const handleSubmitPattern = async (values: PatternFormValues, editing?: ShiftPatternEntity): Promise<boolean> => {
    const sortOrder = editing ? editing.sortOrder : nextSortOrder();
    const body = toRequestBody(values, sortOrder);
    try {
      if (editing) {
        await updatePattern.mutateAsync({ id: editing.id, body });
      } else {
        await createPattern.mutateAsync(body);
      }
      enqueueSnackbar('便を保存しました', { variant: 'success' });
      return true;
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 403) {
        enqueueSnackbar('事業所を選択してください（全社表示では作成できません）', { variant: 'error' });
        return false;
      }
      notifyBulkError('便の保存', error, enqueueSnackbar);
      return false;
    }
  };

  const handleDeletePattern = async (pattern: ShiftPatternEntity): Promise<boolean> => {
    if (!window.confirm(`「${pattern.name}」を削除します。この便を使用中の勤務からは設定が外れます（勤務自体は残ります）。`)) return false;
    try {
      await destroyPattern.mutateAsync(pattern.id);
      enqueueSnackbar('便を削除しました', { variant: 'success' });
      return true;
    } catch {
      enqueueSnackbar('便の削除に失敗しました', { variant: 'error' });
      return false;
    }
  };

  return (
    <TruckShiftsPresenter
      isLoading={isLoading}
      isError={isError}
      trucks={trucks}
      filteredTrucks={filteredTrucks}
      dateColumns={dateColumns}
      period={period}
      rangeLabel={rangeLabel}
      filterOptions={filterOptions}
      filters={filters}
      onPeriodChange={onPeriodChange}
      onPrev={onPrev}
      onNext={onNext}
      onToday={onToday}
      onFiltersChange={setFilters}
      onBack={() => {
        // backTo（呼び出し元の配車画面）＋期間から明示的に戻り先を組み立てる（/shifts と同じ方式）。
        // navigate(-1) だとリロード/共有URL/直接オープン時に元画面へ戻れないため。
        // 戻り先は URL 範囲（＝呼び出し元の範囲）。表示中に期間送りしても呼び出し元の範囲へ戻す。
        const to = backDestinationPath(backTo, initialStartOn, initialEndOn);
        if (to) { void navigate(to); } else { void navigate(-1); }
      }}
      patternManagerOpen={patternManagerOpen}
      onExport={() => { gaEvent('click', 'エクスポート'); void handleExport(); }}
      exportDisabled={exportDisabled}
      onOpenImport={() => { gaEvent('open', 'インポート'); setImportOpen(true); }}
      importDisabled={importDisabled}
      importOpen={importOpen}
      onCloseImport={() => setImportOpen(false)}
      runImport={runImport}
      extractImportError={extractImportError}
      onImportNotify={(message: string, variant: 'success' | 'error') => enqueueSnackbar(message, { variant })}
      onClosePatternManager={() => setPatternManagerOpen(false)}
      patterns={patterns}
      patternSubmitting={createPattern.isPending || updatePattern.isPending}
      onSubmitPattern={handleSubmitPattern}
      onDeletePattern={handleDeletePattern}
      selection={selection}
      selectedDates={selectedDates}
      selectedTruckIds={selectedTruckIds}
      onDatesChange={setSelectedDates}
      selectedTruckCount={selectedTruckIds.size}
      activePatternId={activePatternId}
      onPatternChange={setActivePatternId}
      onAddPattern={() => { gaEvent('open', '便追加'); setPatternManagerOpen(true); }}
      onManagePatterns={() => { gaEvent('open', '便管理'); setPatternManagerOpen(true); }}
      applying={bulkApply.isPending || checking}
      onApply={() => { gaEvent('apply', '一括反映'); void handleApply(); }}
      onRemove={() => { gaEvent('apply', '一括解除'); void handleRemove(); }}
      removing={bulkRemove.isPending || checking}
      removeCount={removeCount}
      onCellClick={handleCellClick}
      cellPopover={cellPopover}
      copy={{
        visible: period === 'week',
        disabled: noCompanySelected || filteredTrucks.length === 0,
        open: copyOpen,
        source: copySource,
        target: { startOn, endOn },
        truckCount: filteredTrucks.length,
        existingCount,
        busy: copyMutation.isPending,
        onOpen: () => { gaEvent('open', 'コピー'); setCopyOpen(true); },
        onClose: () => setCopyOpen(false),
        onCopy: () => { gaEvent('apply', 'コピー実行'); void handleCopy(); },
      }}
      overwriteConfirm={{
        open: confirm !== null,
        action: confirm?.action ?? 'apply',
        shiftCount: confirm?.conflict.shiftCount ?? 0,
        partialMultiDayCount: confirm?.conflict.partialMultiDayCount ?? 0,
        splitAvailable: confirm?.conflict.splitAvailable ?? false,
        wholeAvailable: confirm?.conflict.wholeAvailable ?? false,
        blockedCount: confirm?.conflict.blockedCount ?? 0,
        truckCount: confirm?.body.truck_ids.length ?? 0,
        dateCount: confirm?.body.dates.length ?? 0,
        applying: bulkApply.isPending || bulkRemove.isPending,
        onSplit: () => { gaEvent('apply', '上書き確認 分割'); handleConfirm('split'); },
        onWhole: () => { gaEvent('apply', '上書き確認 全体上書き'); handleConfirm('whole'); },
        onCancel: () => setConfirm(null),
      }}
    />
  );
});

export default Component;
